import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { FailureEffect } from '../../effects';
import { Icon } from '../../foundation/primitives';
import { FailOverlay, JudgementModal, MinigameHud, StartGate } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

// ゼロ config（sandbox 等）の既定デモ。config 不在時の挙動を従来と不変に保つ
const DEFAULT_NODES = [
  { id: 'footprint', label: '足跡', position: { x: '20%', y: '25%' } },
  { id: 'window', label: '割れた窓', position: { x: '70%', y: '20%' } },
  { id: 'camera', label: '監視カメラ', position: { x: '25%', y: '65%' } },
  { id: 'testimony', label: '証言', position: { x: '75%', y: '70%' } },
];
const DEFAULT_CORRECT_CONNECTIONS = [
  ['footprint', 'window'],
  ['camera', 'testimony'],
];
const WRONG_CONNECTION_BEHAVIORS = ['remove', 'shake', 'keep'];

// 非空文字列のみ採用（ホストは raw config を渡す＝Zod default 充填なし。型ゆらぎを自前で吸収する）
const pickText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);

// プレイ条件（timeLimitMs / maxMistakes）の正規化: 正の有限数のみ有効。
// step スキーマの既定 0（=制限なし）と不正値は null に揃える（ゼロ config 回帰安全）
const toPlayLimit = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : null;

// PositionSchema は文字列（'20%' / '120px'）。旧デモ互換で数値・単位なし数字は % とみなす
const toCssLength = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}%`;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return '50%';
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}%`;
    return trimmed;
  }
  return '50%';
};

// 接続は [小さいid, 大きいid] の正規形で持ち、追加・取り消し・照合を全て同じ形で行う
const canonicalPair = (a, b) => (a < b ? [a, b] : [b, a]);
const isSamePair = (a, b) => a[0] === b[0] && a[1] === b[1];

const Template05EvidenceBoard = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_evidence_05', templateId = 5 }) => {
  // T11/T14 と同じ堅牢 config 取り込み: 内容ベースの key で memo し、object identity の揺れに影響されない
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const source = hostConfig ?? {};
    const seenNodeIds = new Set();
    const configNodes = Array.isArray(source.nodes)
      ? source.nodes.filter((node) => {
          if (!node || typeof node.id !== 'string' || node.id === '' || seenNodeIds.has(node.id)) return false;
          seenNodeIds.add(node.id);
          return true;
        })
      : [];
    const usingDefaultNodes = configNodes.length === 0;
    const nodes = usingDefaultNodes ? DEFAULT_NODES : configNodes;
    const knownIds = new Set(nodes.map((node) => node.id));

    // 正解接続: 正規形に揃え、実在ノードのペアだけ残し、重複を除く（必要本数 M と勝利可能性を担保）
    const seenPairs = new Set();
    const configConnections = (Array.isArray(source.correctConnections) ? source.correctConnections : [])
      .filter(
        (pair) => Array.isArray(pair) && typeof pair[0] === 'string' && typeof pair[1] === 'string' && pair[0] !== pair[1]
      )
      .map((pair) => canonicalPair(pair[0], pair[1]))
      .filter((pair) => {
        const key = `${pair[0]}::${pair[1]}`;
        if (!knownIds.has(pair[0]) || !knownIds.has(pair[1]) || seenPairs.has(key)) return false;
        seenPairs.add(key);
        return true;
      });
    const correctConnections =
      configConnections.length > 0
        ? configConnections
        : usingDefaultNodes
          ? DEFAULT_CORRECT_CONNECTIONS.map((pair) => canonicalPair(pair[0], pair[1]))
          : [];

    const successMessage = source.successMessage ?? {};
    const failMessage = source.failMessage ?? {};
    return {
      nodes,
      correctConnections,
      wrongConnectionBehavior: WRONG_CONNECTION_BEHAVIORS.includes(source.wrongConnectionBehavior)
        ? source.wrongConnectionBehavior
        : 'shake',
      connectionStyle: source.connectionStyle && typeof source.connectionStyle === 'object' ? source.connectionStyle : null,
      // R1 体験文法: StartGate / MinigameHud の見出しと説明（config 優先・既定は常体の一行）
      title: pickText(source.title, '証拠ボード'),
      instruction: pickText(source.instructionText, '関係する証拠同士を線で結べ'),
      successTitle:
        typeof successMessage.title === 'string' && successMessage.title !== '' ? successMessage.title : '関連付け完了！',
      successSubtitle:
        typeof successMessage.subtitle === 'string' && successMessage.subtitle !== ''
          ? successMessage.subtitle
          : '証拠の繋がりが見えてきた',
      // failMessage: 未指定は undefined のまま FailOverlay の既定文言（届かなかった…）に委ねる
      failTitle: pickText(failMessage.title, undefined),
      failSubtitle: pickText(failMessage.subtitle, undefined),
      allowRetry: failMessage.allowRetry !== false,
    };
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const [connections, setConnections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [gameState, setGameState] = useState('playing');
  const [feedback, setFeedback] = useState(null);
  const [shakeKey, setShakeKey] = useState(0);
  // R1 体験文法: 開始ゲート。step が showTutorial===false を明示した時だけ最初から開けておく
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  const [mistakes, setMistakes] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [failReason, setFailReason] = useState(null); // 'timeout' | 'mistakes'
  const feedbackTimerRef = useRef(null);
  const { submit } = useManualSubmit(registryId, templateId, hostSubmit);

  // プレイ条件: config 優先 → step(hostMeta)。0 / 不正値は「制限なし」（従来挙動のまま）
  const timeLimitMs = toPlayLimit(hostConfig?.timeLimitMs ?? hostMeta?.timeLimitMs);
  const mistakeLimit = toPlayLimit(hostConfig?.maxMistakes ?? hostMeta?.maxMistakes);

  const failGame = (reason) => {
    setFailReason(reason);
    // success との競合は「先に確定した方が勝つ」（playing の時だけ fail に落とす）
    setGameState((prev) => (prev === 'playing' ? 'fail' : prev));
  };

  // 実動タイマー: ゲート表示中は停止。満了で fail（limit 未設定なら remainingMs=null で無効）
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: !gateOpen && gameState === 'playing',
    onExpire: () => failGame('timeout'),
  });

  useEffect(
    () => () => {
      if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    },
    []
  );

  const clearFeedbackTimer = () => {
    if (feedbackTimerRef.current) {
      window.clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  };

  const showFeedback = (message) => {
    setFeedback(message);
    clearFeedbackTimer();
    feedbackTimerRef.current = window.setTimeout(() => {
      setFeedback(null);
      feedbackTimerRef.current = null;
    }, 1100);
  };

  const handleNodeClick = (nodeId) => {
    if (gameState !== 'playing') return;

    if (selected === null) {
      setSelected(nodeId);
      return;
    }
    if (selected === nodeId) {
      setSelected(null);
      return;
    }
    // 既にある接続と同じ2ノードなら取り消し（誤接続からの復帰経路 その1）
    const pair = canonicalPair(selected, nodeId);
    setConnections((prev) =>
      prev.some((conn) => isSamePair(conn, pair)) ? prev.filter((conn) => !isSamePair(conn, pair)) : [...prev, pair]
    );
    setSelected(null);
  };

  // 全接続を引き直す（誤接続からの復帰経路 その2）
  const clearConnections = () => {
    if (gameState !== 'playing') return;
    setConnections([]);
    setSelected(null);
    setFeedback(null);
    clearFeedbackTimer();
  };

  const checkConnections = () => {
    if (gameState !== 'playing') return;
    const required = resolvedConfig.correctConnections;
    const isCorrect =
      connections.length === required.length && required.every((cc) => connections.some((conn) => isSamePair(conn, cc)));

    if (isCorrect) {
      clearFeedbackTimer();
      setFeedback(null);
      setGameState('success');
      return;
    }
    // 誤接続確定 = ミス 1 回。上限到達時は behavior 反応を出さず FailOverlay（内蔵 shake）へ直行する
    const nextMistakes = mistakes + 1;
    setMistakes(nextMistakes);
    if (mistakeLimit != null && nextMistakes >= mistakeLimit) {
      clearFeedbackTimer();
      setFeedback(null);
      failGame('mistakes');
      return;
    }
    // R0 の wrongConnectionBehavior（既定 shake）と短文トーストは不変
    if (resolvedConfig.wrongConnectionBehavior === 'remove') {
      setConnections((prev) => prev.filter((conn) => required.some((cc) => isSamePair(conn, cc))));
    } else if (resolvedConfig.wrongConnectionBehavior === 'shake') {
      setShakeKey((key) => key + 1);
    }
    showFeedback('繋がりが合わない…');
  };

  // FailOverlay「やり直す」: 内部リセット + retryCount 加算（submit はまだ起きていないので resetSubmission 不要）
  const handleRetry = () => {
    clearFeedbackTimer();
    setConnections([]);
    setSelected(null);
    setFeedback(null);
    setShakeKey(0);
    setMistakes(0);
    setFailReason(null);
    setGameState('playing');
    setRetryCount((count) => count + 1);
    resetTimer();
  };

  // connectionStyle 不在時は従来見た目（幅3・破線 8,4）。明示指定時は schema どおり dashed: true のときだけ破線。
  // 色は config 駆動値をそのまま通す（無指定は .t05-connection-line のトークン色）
  const lineStyleConfig = resolvedConfig.connectionStyle;
  const lineWidth = Number.isFinite(lineStyleConfig?.width) ? lineStyleConfig.width : 3;
  const lineDashArray = lineStyleConfig ? (lineStyleConfig.dashed === true ? '8,4' : undefined) : '8,4';
  const lineColorStyle =
    typeof lineStyleConfig?.color === 'string' && lineStyleConfig.color !== '' ? { stroke: lineStyleConfig.color } : undefined;

  // HUD チップ: showTimer===false / showMistakeCount===false は表示のみ抑止（計時・上限判定は生きる）。
  // 進捗 n/M は確定ボタンが既に持っているため HUD には載せない（二重表示を避ける）
  const hudTimer =
    timeLimitMs != null && remainingMs != null && hostConfig?.showTimer !== false
      ? { remainingMs, totalMs: timeLimitMs }
      : null;
  const hudMistakes =
    mistakeLimit != null && hostConfig?.showMistakeCount !== false
      ? { current: mistakes, max: mistakeLimit }
      : null;

  return (
    <div className="t05-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {!backgroundStyle && <div className="t05-bg-overlay absolute inset-0" />}

      {/* R1 体験文法: 独自絵文字ヘッダ＋飾りメニューを撤去し、共通 HUD に置換。? は開始ゲート再表示 */}
      <MinigameHud
        title={resolvedConfig.title}
        icon="Link2"
        timer={hudTimer}
        mistakes={hudMistakes}
        onHelp={() => setGateOpen(true)}
      />

      <div className="absolute inset-0 pt-14 pb-28">
        <div className="relative w-full h-full p-4">
          <div className="t05-board relative w-full h-full border rounded-xl">
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {connections.map((conn) => {
                const node1 = resolvedConfig.nodes.find((n) => n.id === conn[0]);
                const node2 = resolvedConfig.nodes.find((n) => n.id === conn[1]);
                if (!node1 || !node2) return null;
                return (
                  <line
                    key={`${conn[0]}::${conn[1]}`}
                    className="t05-connection-line"
                    style={lineColorStyle}
                    x1={toCssLength(node1.position?.x ?? node1.x)}
                    y1={toCssLength(node1.position?.y ?? node1.y)}
                    x2={toCssLength(node2.position?.x ?? node2.x)}
                    y2={toCssLength(node2.position?.y ?? node2.y)}
                    strokeWidth={lineWidth}
                    strokeDasharray={lineDashArray}
                  />
                );
              })}
            </svg>

            {resolvedConfig.nodes.map((node) => (
              <button
                key={node.id}
                className={`absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-xl flex flex-col items-center justify-center transition-all border-2
                  ${selected === node.id ? 't05-node--selected scale-110' : 't05-node--idle'}`}
                style={{ left: toCssLength(node.position?.x ?? node.x), top: toCssLength(node.position?.y ?? node.y) }}
                onClick={() => handleNodeClick(node.id)}
              >
                <Icon name="Link2" size={20} className="t05-node-icon" />
                <span className="t05-node-label text-xs mt-1">{node.label ?? node.id}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* R1: 操作説明パネルは StartGate（?で再表示可）に統合し、フッターは操作ボタンのみに絞った */}
      <div className="t05-footer absolute bottom-0 left-0 right-0 z-30 pt-6 pb-6 px-4">
        <div className="flex items-stretch gap-2">
          <button
            className="t05-reset-btn px-4 rounded-xl text-sm"
            onClick={clearConnections}
            disabled={gameState !== 'playing' || connections.length === 0}
          >
            引き直す
          </button>
          <button
            className="t05-confirm-btn flex-1 font-bold py-3 rounded-xl"
            onClick={checkConnections}
          >
            接続を確定（{connections.length}/{resolvedConfig.correctConnections.length}）
          </button>
        </div>
      </div>

      {/* 誤接続確定の瞬間フィードバック（中央トースト）。成功モーダルではないため JudgementModal には集約しない */}
      {feedback && gameState === 'playing' && (
        <div className="t05-feedback absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 px-6 py-3 rounded-xl font-bold pointer-events-none">
          {feedback}
        </div>
      )}

      {gameState === 'playing' && shakeKey > 0 && (
        <FailureEffect key={shakeKey} type="shake" duration={400} onComplete={() => {}} />
      )}

      {/* R1 体験文法: 開始ゲート（表示中は実動タイマーも停止する）。
          旧フッター説明（取り消し操作の一行）はここに統合 */}
      {gateOpen && (
        <StartGate
          title={resolvedConfig.title}
          instruction={
            <>
              {resolvedConfig.instruction}
              <br />
              二つのノードをタップで接続。同じ二つをもう一度選ぶと取り消し
            </>
          }
          icon="Link2"
          onStart={() => setGateOpen(false)}
        />
      )}

      {/* R1 体験文法: fail（時間切れ / ミス上限到達）の明示フィードバック。
          やり直す = 内部リセット / 未整理のまま進む = submit('fail')。文言は config.failMessage 優先 */}
      {gameState === 'fail' && (
        <FailOverlay
          title={resolvedConfig.failTitle}
          subtitle={resolvedConfig.failSubtitle ?? (failReason === 'timeout' ? '時間切れ' : 'ミスが上限に達した')}
          allowRetry={resolvedConfig.allowRetry}
          onRetry={handleRetry}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          旧: Link2 バッジ + 独自オーバーレイ。icons namespace に Link2 は存在＝改名トラップ無し）。
          文言は config.successMessage を優先し、無指定なら従来文言 */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successTitle}
        description={resolvedConfig.successSubtitle}
        icon={<Icon name="Link2" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />
    </div>
  );
};

export default Template05EvidenceBoard;
